import { useState, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Cip,
  cn,
  FiltreKarti,
  Iskelet,
  Metin,
  SatirEylemi,
  SatirEylemleri,
  Tablo,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  notArsivle,
  notGuncelle,
  notlarim,
  notOlustur,
  type KisiselNot,
  type NotAramasi,
} from "./api";

/**
 * NOTLARIM — koçun kendine yazdığı notlar (sürüm 9a; UNİKOÇ "TeacherNotes", tarama-2 §3.13–3.14)
 *
 * ================================================================
 * YALNIZ YAZAN GÖRÜR — KURAL SUNUCUDA
 * ================================================================
 * Matris §5.6 "Koçun özel notu: SELF RCUD"; firma sahibi dahil kimse başkasının
 * notunu göremez. Ekranın bunu sağlamak için yaptığı bir şey YOK: sunucu her
 * sorguyu isteği yapanla süzüyor, görünüm modunda (başka kullanıcı gözüyle)
 * hiç açmıyor (`GORUNUM_KISITI`). Ekran yalnız sunucunun sözünü kullanıcıya
 * söylüyor.
 *
 * Silme yok, arşiv var (kural 7): çöp kutusu düğmesi notu arşivler.
 */

/** UNİKOÇ'taki tür çipleri ve kısayol etiketleri tek listede: etiket olarak saklanıyor. */
const HAZIR_ETIKETLER = [
  { etiket: "hedef", ad: "🎯 Hedef" },
  { etiket: "gözlem", ad: "👀 Gözlem" },
  { etiket: "eylem", ad: "🛠 Eylem adımı" },
  { etiket: "motivasyon", ad: "#motivasyon" },
  { etiket: "program", ad: "#program" },
  { etiket: "deneme", ad: "#deneme" },
  { etiket: "ödev", ad: "#ödev" },
] as const;

const BASLIK_EN_COK = 140;
const ICERIK_EN_COK = 4000;

const tarihSaat = (t: string): string =>
  new Date(t).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type Duzenlenen = { tur: "yeni" } | { tur: "duzenle"; not: KisiselNot };

export function Notlarim() {
  const qc = useQueryClient();
  const [taslak, setTaslak] = useState<NotAramasi>({});
  const [suzgec, setSuzgec] = useState<NotAramasi>({});
  const [duzenlenen, setDuzenlenen] = useState<Duzenlenen | null>(null);
  const [arsivlenecek, setArsivlenecek] = useState<KisiselNot | null>(null);
  const [acik, setAcik] = useState<string | null>(null);
  const suzgecVar = Object.values(suzgec).some((v) => (v ?? "").trim().length > 0);

  const liste = useQuery<KisiselNot[]>({
    queryKey: ["notlarim", suzgec],
    queryFn: () => notlarim(suzgec),
    retry: false,
  });

  const arsivle = useMutation({
    mutationFn: (n: KisiselNot) => notArsivle(n.id, n.surum),
    onSuccess: () => {
      setArsivlenecek(null);
      void qc.invalidateQueries({ queryKey: ["notlarim"] });
    },
    onError: (e) => {
      // Not arada değişti: listeyi tazele ki güncel sürümle yeniden denensin.
      if (e instanceof ApiHatasi && e.kod === "NOT_DEGISTI") {
        void qc.invalidateQueries({ queryKey: ["notlarim"] });
      }
    },
  });

  if (duzenlenen) {
    return (
      <NotFormu
        key={duzenlenen.tur === "yeni" ? "yeni" : duzenlenen.not.id}
        duzenlenen={duzenlenen}
        onKapat={() => setDuzenlenen(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama="Yalnızca sizin görebildiğiniz notlar."
        eylemler={<Buton onClick={() => setDuzenlenen({ tur: "yeni" })}>+ Yeni Not</Buton>}
      />

      <FiltreKarti
        baslik="Filtreler"
        ikon="filtre"
        sutun={3}
        eylemler={
          <>
            <Buton onClick={() => setSuzgec(taslak)}>Ara</Buton>
            <Buton
              gorunum="kesikli"
              onClick={() => {
                setTaslak({});
                setSuzgec({});
              }}
            >
              Temizle
            </Buton>
          </>
        }
      >
        <Alan
          etiket="Arama"
          type="search"
          placeholder="Başlıkta, içerikte ya da etikette ara…"
          maxLength={100}
          value={taslak.ara ?? ""}
          onChange={(e) => setTaslak({ ...taslak, ara: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSuzgec(taslak);
          }}
        />
        <Alan
          etiket="Başlangıç"
          type="date"
          value={taslak.bas ?? ""}
          onChange={(e) => setTaslak({ ...taslak, bas: e.target.value })}
        />
        <Alan
          etiket="Bitiş"
          type="date"
          value={taslak.bit ?? ""}
          onChange={(e) => setTaslak({ ...taslak, bit: e.target.value })}
        />
      </FiltreKarti>

      {arsivlenecek && (
        <div
          role="alertdialog"
          aria-label="Notu arşivle"
          className="rounded-alan bg-uyari-500/10 flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
        >
          <span className="text-uyari-800">
            “{arsivlenecek.baslik}” arşivlenecek ve listeden kalkacak. Not silinmez.
          </span>
          <div className="flex gap-2">
            <Buton
              gorunum="tehlike"
              yukleniyor={arsivle.isPending}
              onClick={() => arsivle.mutate(arsivlenecek)}
            >
              Arşivle
            </Buton>
            <Buton gorunum="sessiz" onClick={() => setArsivlenecek(null)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
      {arsivle.error instanceof ApiHatasi && (
        <p role="alert" className="text-hata-700 text-sm">
          {arsivle.error.message}
        </p>
      )}

      {liste.isPending ? (
        <Iskelet satir={4} />
      ) : liste.isError ? (
        <BosDurum
          ton="hata"
          baslik="Notlar alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        />
      ) : liste.data.length === 0 ? (
        <BosDurum
          baslik={suzgecVar ? "Aramaya uyan not yok" : "Henüz notunuz yok"}
          aciklama={
            suzgecVar
              ? "Aramayı temizleyin ya da tarih aralığını genişletin."
              : "“Yeni Not” ile kendinize bir not yazın. Notlarınızı yalnızca siz görürsünüz."
          }
        />
      ) : (
        <div className="bg-notr-0 border-mor-kenar rounded-[1.5rem] border p-2 shadow-(--golge-unk-kart) md:p-4">
          <Tablo<KisiselNot>
            baslikTuru="degrade"
            satirlar={liste.data}
            anahtar={(n) => n.id}
            altSatir={(n) =>
              acik === n.id ? (
                <div className="flex flex-col gap-2 px-1 py-2 text-sm">
                  {n.etiketler.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {n.etiketler.map((e) => (
                        <Cip key={e} ton="bilgi">
                          #{e}
                        </Cip>
                      ))}
                    </div>
                  )}
                  <p className="text-notr-800 whitespace-pre-line">{n.icerik}</p>
                </div>
              ) : null
            }
            sutunlar={[
              {
                baslik: "Başlık",
                mobil: true,
                hucre: (n) => <span className="text-metin-koyu font-semibold">{n.baslik}</span>,
              },
              { baslik: "Oluşturulma", hucre: (n) => tarihSaat(n.olusturma) },
              { baslik: "Güncelleme", hucre: (n) => tarihSaat(n.guncelleme) },
              {
                baslik: "İşlemler",
                mobil: true,
                etiketGizli: true,
                hucre: (n) => (
                  <SatirEylemleri>
                    <SatirEylemi
                      tur="gor"
                      etiket={`${n.baslik} notunu ${acik === n.id ? "kapat" : "göster"}`}
                      aria-expanded={acik === n.id}
                      onClick={() => setAcik(acik === n.id ? null : n.id)}
                    />
                    <SatirEylemi
                      tur="duzenle"
                      etiket={`${n.baslik} notunu düzenle`}
                      onClick={() => setDuzenlenen({ tur: "duzenle", not: n })}
                    />
                    <SatirEylemi
                      tur="sil"
                      etiket={`${n.baslik} notunu arşivle`}
                      onClick={() => setArsivlenecek(n)}
                    />
                  </SatirEylemleri>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Yeni not / Notu düzenle (UNİKOÇ TeacherNotes-Edit)
// =====================================================================

function NotFormu({ duzenlenen, onKapat }: { duzenlenen: Duzenlenen; onKapat: () => void }) {
  const qc = useQueryClient();
  const mevcut = duzenlenen.tur === "duzenle" ? duzenlenen.not : null;
  const [baslik, setBaslik] = useState(mevcut?.baslik ?? "");
  const [icerik, setIcerik] = useState(mevcut?.icerik ?? "");
  const [etiketler, setEtiketler] = useState<string[]>(mevcut?.etiketler ?? []);

  const kaydet = useMutation({
    mutationFn: () => {
      const veri = { baslik: baslik.trim(), icerik: icerik.trim(), etiketler };
      // Düzenleme EKRANDA GÖRÜLEN sürümle: arada başka sekmede kaydedildiyse 409.
      return mevcut ? notGuncelle(mevcut.id, veri, mevcut.surum) : notOlustur(veri);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notlarim"] });
      onKapat();
    },
  });

  const gecerli = baslik.trim().length > 0 && icerik.trim().length > 0;
  const gonder = () => {
    if (gecerli && !kaydet.isPending) kaydet.mutate();
  };

  // UNİKOÇ kısayolu: Ctrl/Cmd + S kaydet (tarayıcının "sayfayı kaydet"i yerine).
  const kisayol = (e: KeyboardEvent<HTMLFormElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      gonder();
    }
  };

  const etiketDegistir = (e: string) =>
    setEtiketler((l) => (l.includes(e) ? l.filter((x) => x !== e) : [...l, e]));

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama={mevcut ? "Notunuzu güncelleyin." : "Kendinize yeni bir not yazın."}
        eylemler={
          <Buton
            gorunum="sessiz"
            onClick={onKapat}
            className="text-mor-700 font-semibold underline"
          >
            ← Notlara Dön
          </Buton>
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          gonder();
        }}
        onKeyDown={kisayol}
        className="bg-notr-0 border-mor-kenar flex flex-col gap-4 rounded-[1.5rem] border p-4 shadow-(--golge-unk-kart) md:p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Etiketler">
            {HAZIR_ETIKETLER.map((h) => {
              const secili = etiketler.includes(h.etiket);
              return (
                <button
                  key={h.etiket}
                  type="button"
                  aria-pressed={secili}
                  onClick={() => etiketDegistir(h.etiket)}
                  className={cn(
                    "rounded-rozet min-h-9 px-3 py-1 text-xs font-semibold ring-1 ring-inset",
                    secili
                      ? "bg-mor-50 text-mor-700 ring-mor-500/40"
                      : "bg-notr-0 text-notr-700 ring-notr-200",
                  )}
                >
                  {h.ad}
                </button>
              );
            })}
          </div>
          <span className="text-notr-600 text-xs">Kısayol: Ctrl/Cmd + S kaydet</span>
        </div>

        <Alan
          etiket="Başlık"
          zorunlu
          maxLength={BASLIK_EN_COK}
          value={baslik}
          onChange={(e) => setBaslik(e.target.value)}
          yardim={`Açık ve eylem odaklı bir başlık seçin. ${baslik.length}/${BASLIK_EN_COK}`}
        />
        <Metin
          etiket="İçerik"
          rows={8}
          maxLength={ICERIK_EN_COK}
          sayacGoster
          value={icerik}
          onChange={(e) => setIcerik(e.target.value)}
          yardim="Sonraki adımı net yazın; tarih ve sınav adı gibi anahtar bilgileri ekleyin."
        />

        {mevcut && (
          <div className="text-notr-600 flex flex-wrap gap-3 text-xs">
            <span>Oluşturulma: {tarihSaat(mevcut.olusturma)}</span>
            <span>Son güncelleme: {tarihSaat(mevcut.guncelleme)}</span>
          </div>
        )}

        {kaydet.error instanceof ApiHatasi && (
          <p role="alert" className="text-hata-700 text-sm">
            {kaydet.error.message}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" disabled={!gecerli} yukleniyor={kaydet.isPending}>
            {mevcut ? "Güncelle" : "Kaydet"}
          </Buton>
          <Buton gorunum="sessiz" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </div>
  );
}
